/**
 * Cost Report engine — shared by the top-nav Cost report page and the per-job
 * Report tab. All options default to the "punchier" setup: this week, charge-out
 * rates ON, margin OFF, pro-rata OFF.
 */
import { useEffect, useMemo, useState } from "react";
import api, { fmtNZD, API, nzISODate } from "@/lib/api";
import NumberInput from "@/components/NumberInput";
import DateInput from "@/components/DateInput";

function thisWeek() {
  const now = new Date(`${nzISODate()}T00:00:00Z`);
  const day = (now.getUTCDay() + 6) % 7; // Mon=0
  const monday = new Date(now); monday.setUTCDate(now.getUTCDate() - day);
  const sunday = new Date(monday); sunday.setUTCDate(monday.getUTCDate() + 6);
  return [monday.toISOString().slice(0, 10), sunday.toISOString().slice(0, 10)];
}

export default function CostReportInner({ forcedProjectId = null }) {
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState(forcedProjectId || "");
  const [df, setDf] = useState(() => thisWeek()[0]);
  const [dt, setDt] = useState(() => thisWeek()[1]);
  const [chargeout, setChargeout] = useState(true);
  const [unreportedOnly, setUnreportedOnly] = useState(true);
  const [marginMode, setMarginMode] = useState("off");
  const [mMat, setMMat] = useState(0); const [mLab, setMLab] = useState(0);
  const [mSub, setMSub] = useState(0); const [mAll, setMAll] = useState(0);
  const [proRata, setProRata] = useState(false);
  const [report, setReport] = useState(null);
  const [exBill, setExBill] = useState(new Set());
  const [exTs, setExTs] = useState(new Set());
  const [manualItems, setManualItems] = useState([]);
  const [editItems, setEditItems] = useState(false);
  const [committed, setCommitted] = useState(null); // {undo_id, totals}
  const [err, setErr] = useState("");

  useEffect(() => {
    if (forcedProjectId) return;
    api.get("/projects").then(r => setProjects(r.data));
  }, [forcedProjectId]);

  const query = useMemo(() => ({
    project_id: pid, date_from: df, date_to: dt,
    unreported_only: unreportedOnly, chargeout,
    margin_mode: marginMode,
    margin_materials: mMat, margin_labour: mLab, margin_subs: mSub,
    margin_overall: mAll, pro_rata: proRata,
    manual_items: manualItems,
    exclude_bill_ids: Array.from(exBill),
    exclude_timesheet_entry_ids: Array.from(exTs),
  }), [pid, df, dt, chargeout, unreportedOnly, marginMode, mMat, mLab, mSub, mAll, proRata, manualItems, exBill, exTs]);

  async function run() {
    if (!pid) return;
    setErr("");
    try {
      const { data } = await api.post("/cost-report", query);
      setReport(data);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not build report"); }
  }
  useEffect(() => { if (pid) run(); /* eslint-disable-next-line */ }, [pid]);

  async function downloadPdf() {
    const r = await api.post("/cost-report/pdf", query, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    window.open(url, "_blank");
  }

  async function markReported() {
    if (!report) return;
    const bill_ids = [
      ...report.sections.materials.rows.map(r => r.id).filter(Boolean),
      ...report.sections.subcontractors.rows.map(r => r.id).filter(Boolean),
    ].filter(id => !exBill.has(id));
    const hour_keys = report.sections.labour.rows
      .flatMap(r => r.hour_keys || [])
      .filter(hk => !exTs.has(hk));
    const { data } = await api.post("/cost-report/commit", {
      project_id: pid, date_from: df, date_to: dt,
      bill_ids, hour_keys, total: report.total_cost,
    });
    setCommitted({ undo_id: data.undo_id, total: report.total_cost });
    run();
  }
  async function undoReported() {
    if (!committed) return;
    await api.post(`/cost-report/undo/${committed.undo_id}`);
    setCommitted(null); run();
  }

  function toggleBill(id) { const s = new Set(exBill); s.has(id)?s.delete(id):s.add(id); setExBill(s); }
  function toggleTs(key) { const s = new Set(exTs); s.has(key)?s.delete(key):s.add(key); setExTs(s); }

  return (
    <div className="space-y-3" data-testid="cost-report-inner">
      <div className="card flex gap-2 items-end flex-wrap">
        {!forcedProjectId && (
          <label className="text-sm">Project
            <select className="input" value={pid} onChange={e=>setPid(e.target.value)} data-testid="cr-project">
              <option value="">—</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select></label>
        )}
        <label className="text-sm">From<DateInput className="input" data-testid="cr-from" value={df} onChange={e=>setDf(e.target.value)} showQuickPicks onQuickPick={({from,to})=>{setDf(from);setDt(to);}}/></label>
        <label className="text-sm">To<DateInput className="input" data-testid="cr-to" value={dt} onChange={e=>setDt(e.target.value)}/></label>
        <label className="text-sm flex items-center gap-1" data-testid="cr-chargeout">
          <input type="checkbox" checked={chargeout} onChange={e=>setChargeout(e.target.checked)}/> Use charge-out rates
        </label>
        <label className="text-sm flex items-center gap-1" data-testid="cr-unreported">
          <input type="checkbox" checked={unreportedOnly} onChange={e=>setUnreportedOnly(e.target.checked)}/> Unreported only
        </label>
        <button className="btn-silver" onClick={run} data-testid="btn-cr-run">Build</button>
        {report && (
          <button className="btn-silver" onClick={downloadPdf} data-testid="btn-cr-pdf">Download PDF</button>
        )}
      </div>

      <div className="card flex gap-2 items-end flex-wrap">
        <label className="text-sm">Margin
          <select className="input" value={marginMode} onChange={e=>setMarginMode(e.target.value)} data-testid="cr-margin-mode">
            <option value="off">Off (default)</option>
            <option value="per_section">Per section</option>
            <option value="overall">One overall</option>
          </select></label>
        {marginMode === "per_section" && (
          <>
            <label className="text-xs">Materials %<NumberInput className="input w-16" value={mMat} onChange={e=>setMMat(parseFloat(e.target.value||0))} data-testid="cr-m-mat"/></label>
            <label className="text-xs">Labour %<NumberInput className="input w-16" value={mLab} onChange={e=>setMLab(parseFloat(e.target.value||0))} data-testid="cr-m-lab"/></label>
            <label className="text-xs">Subs %<NumberInput className="input w-16" value={mSub} onChange={e=>setMSub(parseFloat(e.target.value||0))} data-testid="cr-m-sub"/></label>
          </>
        )}
        {marginMode === "overall" && (
          <>
            <label className="text-xs">Overall %<NumberInput className="input w-20" value={mAll} onChange={e=>setMAll(parseFloat(e.target.value||0))} data-testid="cr-m-all"/></label>
            <label className="text-xs flex items-center gap-1">
              <input type="checkbox" checked={proRata} onChange={e=>setProRata(e.target.checked)} data-testid="cr-pro-rata"/> Pro-rata
            </label>
          </>
        )}
      </div>

      {err && <div className="card text-red-400 text-sm">{err}</div>}
      {!pid && !forcedProjectId && <div className="opacity-60">Pick a project to build the cost report.</div>}

      {report && (
        <div data-testid="cr-report">
          <div className="card border border-silver/60">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-silver">{report.project?.name} — Cost report {report.date_from} → {report.date_to}</h3>
              <button className="btn-ghost text-xs" onClick={()=>setEditItems(x=>!x)} data-testid="btn-cr-edit">
                {editItems ? "Lock items" : "Edit items"}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2 text-sm">
              <div>Total cost (ex GST): <b data-testid="cr-total">{fmtNZD(report.total_cost)}</b></div>
              <div>Labour hours: <b data-testid="cr-hours">{report.sections.labour.hours_total.toFixed(2)} h</b></div>
              <div>Rate used: <b>{fmtNZD(report.rate_used)}/h</b></div>
            </div>
          </div>

          <Section title="Materials bills" section={report.sections.materials}
                   editItems={editItems} ex={exBill} toggle={toggleBill}
                   rowLabel={r => `${r.supplier} #${r.number||"—"} (${r.issue_date||"—"})`}/>
          <Section title="Labour" section={report.sections.labour}
                   editItems={editItems} ex={exTs} toggle={toggleTs}
                   rowKey={r => (r.hour_keys || [])[0]}
                   rowLabel={r => `${r.worker_name} — ${r.hours}h × ${fmtNZD(r.rate)}`}/>
          {report.sections.labour.by_task?.length > 0 && (
            <div className="card" data-testid="cr-hours-by-task">
              <h4 className="text-silver mb-1 text-sm">Hours by programme task</h4>
              {report.sections.labour.by_task.map(t => (
                <div key={t.programme_item_id} className="flex justify-between text-sm border-b border-neutral-800 py-1" data-testid={`cr-task-${t.programme_item_id}`}>
                  <span>{t.task}</span><span>{t.hours.toFixed(2)} h</span>
                </div>
              ))}
            </div>
          )}
          <Section title="Subcontractor bills" section={report.sections.subcontractors}
                   editItems={editItems} ex={exBill} toggle={toggleBill}
                   rowLabel={r => `${r.supplier} #${r.number||"—"} (${r.issue_date||"—"})`}/>

          <div className="card" data-testid="cr-manual">
            <h4 className="text-silver mb-1 text-sm">Manual items</h4>
            {manualItems.map((m, i) => (
              <div key={i} className="flex gap-2 mb-1">
                <select className="input" value={m.section||"Materials"} onChange={e=>setManualItems(x=>x.map((y,j)=>j===i?{...y,section:e.target.value}:y))}>
                  <option>Materials</option><option>Labour</option><option>Subcontractors</option>
                </select>
                <input className="input flex-1" placeholder="Description" value={m.description||""}
                       onChange={e=>setManualItems(x=>x.map((y,j)=>j===i?{...y,description:e.target.value}:y))}/>
                <NumberInput className="input w-24" money value={m.amount||0}
                       onChange={e=>setManualItems(x=>x.map((y,j)=>j===i?{...y,amount:parseFloat(e.target.value||0)}:y))}/>
                <button className="text-xs text-red-400" onClick={()=>setManualItems(x=>x.filter((_,j)=>j!==i))}>Remove</button>
              </div>
            ))}
            <button className="btn-ghost text-xs" onClick={()=>setManualItems(x=>[...x,{section:"Materials",description:"",amount:0}])}
                    data-testid="btn-add-manual">+ Add manual item</button>
            <button className="btn-silver text-xs ml-2" onClick={run}>Recalculate</button>
          </div>

          <div className="card">
            <div className="flex justify-between text-sm"><span>Subtotal (ex GST)</span><span>{fmtNZD(report.sections_subtotal)}</span></div>
            <div className="flex justify-between text-sm"><span>Margin</span><span>{fmtNZD(report.margin_total)}</span></div>
            <div className="flex justify-between text-silver font-bold mt-1"><span>Total cost</span><span data-testid="cr-footer-total">{fmtNZD(report.total_cost)}</span></div>
            <div className="flex justify-between text-sm"><span>GST (15%)</span><span>{fmtNZD(report.gst)}</span></div>
            <div className="flex justify-between text-silver font-bold text-lg"><span>TOTAL INC GST</span><span>{fmtNZD(report.total_inc_gst)}</span></div>
            <div className="flex gap-2 mt-3 flex-wrap">
              {!committed && (
                <button className="btn-silver" onClick={markReported} data-testid="btn-mark-reported">
                  Mark these items as reported
                </button>
              )}
              {committed && (
                <button className="btn-ghost text-red-400" onClick={undoReported} data-testid="btn-undo-reported">
                  Undo — unmark reported ({fmtNZD(committed.total)})
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, section, editItems, ex, toggle, rowLabel, rowKey }) {
  const key = (r) => rowKey ? rowKey(r) : r.id;
  return (
    <div className="card" data-testid={`cr-section-${title.toLowerCase().split(" ")[0]}`}>
      <h4 className="text-silver text-sm">{title}</h4>
      {section.rows.length === 0 ? (
        <div className="opacity-50 text-xs">Nothing in this period.</div>
      ) : (
        <ul className="text-sm mt-1 space-y-0.5">
          {section.rows.map(r => (
            <li key={key(r)} className="flex gap-2 items-center">
              {editItems && (
                <input type="checkbox" checked={!ex.has(key(r))} onChange={()=>toggle(key(r))}/>
              )}
              <span className="flex-1">{rowLabel(r)}</span>
              <span>{fmtNZD(r.total)}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="text-xs opacity-70 mt-1">
        Subtotal: {fmtNZD(section.subtotal)} · Margin: {fmtNZD(section.margin)} · <b>Section total: {fmtNZD(section.total)}</b>
      </div>
    </div>
  );
}
